import { useRef } from "react";
import { toast } from "sonner";
import { FileText, Upload, Trash2 } from "lucide-react";

/**
 * Inline rent-receipt attach/view/replace control. Reads + writes the
 * `rent_receipt_url` + `rent_receipt_uploaded_at` keys on the employee form.
 * Image or PDF, max 1MB, base64-encoded into the form so the parent dialog
 * can submit it with the rest of the employee data in a single PUT.
 */
export default function RentReceiptUploader({ form, setForm }) {
    const inputRef = useRef(null);
    const onPick = () => inputRef.current?.click();
    const onFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 1_048_576) {
            toast.error("Receipt exceeds 1MB. Please compress before upload.");
            return;
        }
        if (!file.type.match(/^(image\/|application\/pdf)/)) {
            toast.error("Only images or PDF receipts allowed");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setForm({
                ...form,
                rent_receipt_url: reader.result,
                rent_receipt_uploaded_at: new Date().toISOString(),
            });
            toast.success(`${file.name} attached. Save the form to persist.`);
        };
        reader.readAsDataURL(file);
    };
    const onClear = () => setForm({ ...form, rent_receipt_url: "", rent_receipt_uploaded_at: "" });
    const hasReceipt = !!form.rent_receipt_url;
    const expired = form.rent_receipt_expiry && new Date(form.rent_receipt_expiry) < new Date();
    return (
        <div className="flex items-center gap-3" data-testid="rent-receipt-uploader">
            <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="rent-receipt-file" />
            {hasReceipt ? (
                <div className="flex items-center gap-2 flex-1">
                    <div className={`inline-flex items-center gap-2 px-3 py-2 border rounded text-xs ${expired ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                        <FileText className="w-3.5 h-3.5" />
                        {expired ? "Receipt EXPIRED — renew" : "Receipt attached"}
                        {form.rent_receipt_uploaded_at && (
                            <span className="opacity-60">· {new Date(form.rent_receipt_uploaded_at).toLocaleDateString()}</span>
                        )}
                    </div>
                    <a href={form.rent_receipt_url} target="_blank" rel="noreferrer" className="text-xs text-klein hover:underline" data-testid="rent-receipt-view">
                        View
                    </a>
                    <button type="button" onClick={onPick} className="text-xs text-klein hover:underline" data-testid="rent-receipt-replace">
                        Replace
                    </button>
                    <button type="button" onClick={onClear} className="text-ink/40 hover:text-red-700" data-testid="rent-receipt-clear">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={onPick}
                    className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5 rounded"
                    data-testid="rent-receipt-upload-btn"
                >
                    <Upload className="w-4 h-4" /> Choose file
                </button>
            )}
        </div>
    );
}
